import type { UpcomingVisit } from '@/api/types/ProviderApi'
import { CardRule } from '@/components/VisitPage/CardRule'
import { Glyph } from '@/components/svgs/Glyph'
import { colors } from '@/constants/colors'
import { type VisitStateRow, visitStateRow } from '@/features/visits/stateRow'
import { useDiscColor } from '@/ui/core/card-ground'
import { VStack } from '@/ui/core/stack'
import { Text } from '@/ui/core/typography'
import { View } from 'react-native'

/**
 * Where the visit stands, as the last row of the page's list.
 *
 * Not a `Pressable`: it opens nothing, so it carries no chevron and nothing about it invites a
 * thumb. Every other line in this list is a door and this one is a statement, which the missing
 * chevron is enough to say — the same rule a visit with no results follows in Care's list.
 *
 * Its own file rather than a branch inside `VisitSlot`, which is where the checked-in version of
 * this row lived: the slot is the card's action and this is the page's status, and the slot had
 * grown a second job that only one of its two callers wanted.
 */
export const StateRow = ({
	visit,
	now = new Date(),
}: { visit: UpcomingVisit; now?: Date }) => {
	const state = visitStateRow(visit, now)
	const disc = useDiscColor()

	if (!state) {
		return null
	}

	return <Row state={state} disc={disc} />
}

const Row = ({ state, disc }: { state: VisitStateRow; disc: string }) => (
	<View className="flex-row items-center px-4 py-4" style={{ gap: 12 }}>
		<CardRule />
		<View
			className="h-10 w-10 items-center justify-center rounded-full"
			style={{ backgroundColor: disc }}
		>
			{/* A clock for anything about a window, a tick for anything settled. Black, like every
			    other mark in this list: the green that the checked-in state wore was the one spend
			    of colour on the screen at the one moment nothing is being asked of anybody. */}
			<Glyph name={state.glyph} size={19} color={colors.ink} />
		</View>
		<VStack gap={2} className="flex-1">
			<Text size="body0" weight="bold">
				{state.title}
			</Text>
			<Text size="body2" tone="muted">
				{state.line}
			</Text>
		</VStack>
	</View>
)
